import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { getApiUrl } from '../config/api.ts';
import { createLogger, toErrorMessage } from '@metron/shared';

const logger = createLogger('useAuditLogs');

// See packages/shared/src/types/audit.ts for source of truth
export interface AuditEvent {
  eventId: string;
  timestamp: string;
  userId: string;
  username: string;
  userGroups: string[];
  action: string;
  resource: string;
  resourceType: string;
  ipAddress: string;
  userAgent: string;
  status: 'success' | 'failure';
  statusCode?: number;
  errorMessage?: string;
  requestId: string;
  impersonatedRole?: string;
  impersonatedGroups?: string[];
  impersonationSessionId?: string;
}

export interface AuditLogsFilters {
  userId?: string;
  username?: string;
  action?: string;
  resourceType?: string;
  startDate?: string;
  endDate?: string;
  status?: 'success' | 'failure';
  limit?: number;
  nextToken?: string;
}

interface AuditLogsResponse {
  events: AuditEvent[];
  nextToken?: string;
}

export function useAuditLogs(filters: AuditLogsFilters = {}) {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const { getAccessToken } = useAuth();

  const fetchAuditLogs = async (filtersToApply: AuditLogsFilters = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      // Build query parameters
      const params = new URLSearchParams();
      if (filtersToApply.userId) params.append('userId', filtersToApply.userId);
      if (filtersToApply.username) params.append('username', filtersToApply.username);
      if (filtersToApply.action) params.append('action', filtersToApply.action);
      if (filtersToApply.resourceType) params.append('resourceType', filtersToApply.resourceType);
      if (filtersToApply.startDate) params.append('startDate', filtersToApply.startDate);
      if (filtersToApply.endDate) params.append('endDate', filtersToApply.endDate);
      if (filtersToApply.status) params.append('status', filtersToApply.status);
      if (filtersToApply.limit) params.append('limit', filtersToApply.limit.toString());
      if (filtersToApply.nextToken) params.append('nextToken', filtersToApply.nextToken);

      const queryString = params.toString();
      const url = getApiUrl(`audit-logs${queryString ? `?${queryString}` : ''}`);

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to fetch audit logs');
      }

      const data: AuditLogsResponse = await response.json();
      setEvents(data.events);
      setNextToken(data.nextToken);
    } catch (err) {
      const errorMessage = toErrorMessage(err, 'Failed to fetch audit logs');
      setError(errorMessage);
      logger.error({ err }, 'Error fetching audit logs');
    } finally {
      setIsLoading(false);
    }
  };

  const exportAuditLogs = async (format: 'csv' | 'json', filtersToApply: AuditLogsFilters = {}) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      // Build query parameters
      const params = new URLSearchParams();
      params.append('format', format);
      if (filtersToApply.userId) params.append('userId', filtersToApply.userId);
      if (filtersToApply.username) params.append('username', filtersToApply.username);
      if (filtersToApply.action) params.append('action', filtersToApply.action);
      if (filtersToApply.resourceType) params.append('resourceType', filtersToApply.resourceType);
      if (filtersToApply.startDate) params.append('startDate', filtersToApply.startDate);
      if (filtersToApply.endDate) params.append('endDate', filtersToApply.endDate);
      if (filtersToApply.status) params.append('status', filtersToApply.status);

      const queryString = params.toString();
      const url = getApiUrl(`audit-logs/export?${queryString}`);

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to export audit logs');
      }

      // Create a blob from the response and trigger download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `audit-logs-${Date.now()}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      const errorMessage = toErrorMessage(err, 'Failed to export audit logs');
      logger.error({ err }, 'Error exporting audit logs');
      throw new Error(errorMessage);
    }
  };

  useEffect(() => {
    fetchAuditLogs(filters);
  }, [
    filters.userId,
    filters.username,
    filters.action,
    filters.resourceType,
    filters.startDate,
    filters.endDate,
    filters.status,
    filters.limit,
    filters.nextToken,
  ]);

  return {
    events,
    isLoading,
    error,
    nextToken,
    refetch: fetchAuditLogs,
    exportAuditLogs,
  };
}
